import React from "react";
import ReactDatePicker from "react-datepicker";
import timezones from "../nodes/Logic/timezones";
import Input from "../nodes/Logic/Inputs/Input";

export const businessHoursDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const getDefaultBusinessDay = () => ({
  Business: "hours",
  Start: "09:00",
  End: "17:00",
});

export const getDefaultBusinessHours = () =>
  businessHoursDays.reduce(
    (hours, day) => ({
      ...hours,
      [day]: getDefaultBusinessDay(),
    }),
    { TimeZone: "" }
  );

const parseTime = (value) =>
  new Date(new Date().setHours(...(value || "12:00").split(":")));

const formatTime = (date) =>
  `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

const APPLY_BUSINESS_HOURS_ALWAYS = "always";
const APPLY_BUSINESS_HOURS_CONDITIONALLY = "when-short-text-matches";

export default function BusinessHoursForm({
  value,
  onChange,
  showLabel = false,
  labelTitle = "Business hours label",
  variableOptions = [],
  conditionVariableOptions = [],
}) {
  const businessHours = {
    ...getDefaultBusinessHours(),
    ...(value || {}),
  };
  const [applyBusinessHoursMode, setApplyBusinessHoursMode] = React.useState(
    businessHours?.ConditionInputArgumentUUID
      ? APPLY_BUSINESS_HOURS_CONDITIONALLY
      : APPLY_BUSINESS_HOURS_ALWAYS
  );
  const [rowErrors, setRowErrors] = React.useState({});

  const setBusinessHours = (fn) => onChange(fn(businessHours));

  return (
    <>
      <div className="row mb-3">
        {showLabel && (
          <div className="col-sm">
            <label className="form-label">{labelTitle}</label>
            <input
              className="form-control mb-3"
              title={labelTitle}
              placeholder="Business hours"
              type="text"
              value={businessHours?.Label ?? ""}
              onChange={(e) =>
                setBusinessHours((prev) => ({
                  ...prev,
                  Label: e.target.value,
                }))
              }
            />
          </div>
        )}
        <div className="col-sm">
          <label className="form-label">Time zone</label>
          <Input
            inputType="select"
            isValidNewOption={() => false}
            value={[businessHours?.TimeZone || ""]}
            onChange={(values) =>
              setBusinessHours((prev) => ({
                ...prev,
                TimeZone: values[0],
              }))
            }
            options={[
              {
                value: "",
                label: "Project default time zone",
              },
              ...timezones.map((tz) => ({
                value: tz.tzCode,
                label: tz.label,
              })),
            ]}
          />
        </div>
        {variableOptions.length > 0 && (
          <div className="col-sm">
            <label className="form-label">Load business hours from</label>
            <Input
              inputType="select"
              isValidNewOption={() => false}
              isClearable
              value={[businessHours?.InputArgumentUUID || ""]}
              onChange={(values) =>
                setBusinessHours((prev) => ({
                  ...prev,
                  InputArgumentUUID: values[0] || "",
                }))
              }
              options={[
                { value: "", label: "Use this business hours form" },
                ...variableOptions.map((option) => ({
                  ...option,
                  label: `Load from variable: ${option.label}`,
                })),
              ]}
            />
          </div>
        )}
      </div>

      {conditionVariableOptions.length > 0 && (
        <div className="row mb-3">
          <div className="col-sm">
            <label className="form-label">Apply business hours</label>
            <Input
              inputType="radio"
              radioName="applyBusinessHoursMode"
              isValidNewOption={() => false}
              value={[applyBusinessHoursMode]}
              onChange={(values) => {
                setApplyBusinessHoursMode(values[0]);
                setBusinessHours((prev) => ({
                  ...prev,
                  ...(values[0] === APPLY_BUSINESS_HOURS_ALWAYS
                    ? {
                        ConditionInputArgumentUUID: "",
                        ConditionValue: "",
                      }
                    : {}),
                }));
              }}
              options={[
                { value: APPLY_BUSINESS_HOURS_ALWAYS, label: "Always" },
                {
                  value: APPLY_BUSINESS_HOURS_CONDITIONALLY,
                  label: "When short-text matches",
                },
              ]}
            />
          </div>
          {applyBusinessHoursMode === APPLY_BUSINESS_HOURS_CONDITIONALLY && (
            <>
              <div className="col-sm">
                <label className="form-label">Short-text variable</label>
                <Input
                  inputType="select"
                  isValidNewOption={() => false}
                  isClearable
                  value={[businessHours?.ConditionInputArgumentUUID || ""]}
                  onChange={(values) => {
                    setApplyBusinessHoursMode(
                      values[0]
                        ? APPLY_BUSINESS_HOURS_CONDITIONALLY
                        : APPLY_BUSINESS_HOURS_ALWAYS
                    );
                    setBusinessHours((prev) => ({
                      ...prev,
                      ConditionInputArgumentUUID: values[0] || "",
                      ...(values[0] ? {} : { ConditionValue: "" }),
                    }));
                  }}
                  options={conditionVariableOptions}
                />
              </div>
              <div className="col-sm">
                <label className="form-label">Matching value</label>
                <input
                  className="form-control"
                  type="text"
                  value={businessHours?.ConditionValue || ""}
                  disabled={!businessHours?.ConditionInputArgumentUUID}
                  onChange={(e) =>
                    setBusinessHours((prev) => ({
                      ...prev,
                      ConditionValue: e.target.value,
                    }))
                  }
                />
              </div>
            </>
          )}
        </div>
      )}

      {businessHoursDays.map((day, index) => (
        <div
          key={day}
          className="d-grid gap-2 mb-2"
          style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
        >
          <div>
            <div>{day}</div>
            {index === 0 && (
              <button
                className="btn btn-link btn-sm d-block p-0 mt-1 align-baseline text-start text-primary"
                type="button"
                onClick={() => {
                  const monday = businessHours?.Monday || getDefaultBusinessDay();
                  setRowErrors({});
                  setBusinessHours((prev) => ({
                    ...prev,
                    Monday: monday,
                    Tuesday: monday,
                    Wednesday: monday,
                    Thursday: monday,
                    Friday: monday,
                    Saturday: monday,
                    Sunday: monday,
                  }));
                }}
              >
                copy to all
              </button>
            )}
          </div>
          <div>
            <select
              className="form-select"
              aria-label={`${day} business hours type`}
              value={businessHours?.[day]?.Business || "hours"}
              onChange={(e) => {
                setRowErrors((prev) => ({ ...prev, [day]: "" }));
                setBusinessHours((prev) => ({
                  ...prev,
                  [day]: {
                    ...getDefaultBusinessDay(),
                    ...prev[day],
                    Business: e.target.value,
                  },
                }));
              }}
            >
              <option value="all-day">All day</option>
              <option value="no-business">No business</option>
              <option value="hours">Business hours</option>
            </select>
          </div>
          {["Start", "End"].map((time) => (
            <div key={`${day}-${time}`}>
              <ReactDatePicker
                className="form-control"
                wrapperClassName="w-100"
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                dateFormat="h:mm a"
                disabled={businessHours?.[day]?.Business !== "hours"}
                selected={parseTime(businessHours?.[day]?.[time])}
                onChange={(date) => {
                  if (
                    time === "Start" &&
                    date > parseTime(businessHours?.[day]?.End)
                  ) {
                    setRowErrors((prev) => ({
                      ...prev,
                      [day]: "Start cannot be after end.",
                    }));
                    return;
                  }
                  if (
                    time === "End" &&
                    date < parseTime(businessHours?.[day]?.Start)
                  ) {
                    setRowErrors((prev) => ({
                      ...prev,
                      [day]: "End cannot be before start.",
                    }));
                    return;
                  }
                  setRowErrors((prev) => ({ ...prev, [day]: "" }));
                  setBusinessHours((prev) => ({
                    ...prev,
                    [day]: {
                      ...getDefaultBusinessDay(),
                      ...prev[day],
                      [time]: formatTime(date),
                    },
                  }));
                }}
              />
            </div>
          ))}
          {rowErrors[day] && (
            <div className="text-danger small" style={{ gridColumn: "1 / -1" }}>
              {rowErrors[day]}
            </div>
          )}
        </div>
      ))}
    </>
  );
}